import { useState, useMemo, useEffect } from "react"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import MenuItem from "@mui/material/MenuItem"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import CircularProgress from "@mui/material/CircularProgress"
import CenteredModal from "@/src/common/centered-modal/CenteredModal"
import { Iconify } from "@/src/common/iconify"
import { Scrollbar } from "@/src/common/scrollbar"
import { toast } from "sonner"
import { getAllUsers, linkUserToWorkspace } from "@/src/services/internal"
import { useInternalStore } from "@/src/store"
import { extractErrorMessage, getResponseError } from "@/src/utils/api-error"
import {
    WORKSPACE_USER_ROLE_OPTIONS,
    DEFAULT_WORKSPACE_ROLE,
    type WorkspaceRoleValue,
} from "@/src/features/internalUsers/constants"

type LinkableUser = { id: string; name: string; email: string }
export interface LinkExistingUserModalProps {
    open: boolean
    onClose: () => void
    onSuccess?: () => void
}

export function LinkExistingUserModal({ open, onClose, onSuccess }: LinkExistingUserModalProps) {
    const [searchQuery, setSearchQuery] = useState("")
    const [users, setUsers] = useState<LinkableUser[]>([])
    const [loading, setLoading] = useState(false)
    const [selectedRoleByUserId, setSelectedRoleByUserId] = useState<Record<string, WorkspaceRoleValue>>({})
    const selectedWorkspaceId = useInternalStore((state) => state.selectedWorkspaceId)
    const workspaceUsers = useInternalStore((state) => state.workspaceUsers)
    const alreadyAddedUserIds = useMemo(() => new Set(workspaceUsers.map((u) => u.userId)), [workspaceUsers])

    useEffect(() => {
        if (!open) return
        setLoading(true)
        getAllUsers()
            .then((res) => {
                const list: LinkableUser[] = (res.data?.content ?? []).map((u) => ({
                    id: u.userId,
                    name: u.fullName,
                    email: u.email,
                }))
                setUsers(list)
            })
            .catch(() => {
                toast.error("Failed to load users")
            })
            .finally(() => setLoading(false))
    }, [open])

    const handleClose = () => {
        setSearchQuery("")
        setSelectedRoleByUserId({})
        onClose()
    }

    const getRoleForUser = (userId: string) => selectedRoleByUserId[userId] ?? DEFAULT_WORKSPACE_ROLE
    const setRoleForUser = (userId: string, value: WorkspaceRoleValue) =>
        setSelectedRoleByUserId((previousRoles) => ({ ...previousRoles, [userId]: value }))

    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return users
        const q = searchQuery.toLowerCase()
        return users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    }, [users, searchQuery])

    const handleAddUser = async (userId: string) => {
        if (!selectedWorkspaceId) return
        const roleType = getRoleForUser(userId)
        try {
            const result = await linkUserToWorkspace(selectedWorkspaceId, { users: [{ userId, roleType }] })
            const errorMsg = getResponseError(result)
            if (errorMsg) {
                toast.error(errorMsg)
                return
            }
            onSuccess?.()
            handleClose()
        } catch (err) {
            const message = extractErrorMessage(err) || "Failed to link user to workspace"
            toast.error(message)
            console.error("Error linking user to workspace:", err)
        }
    }

    return (
        <CenteredModal open={open} onClose={handleClose} width={600}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0, "& > *": { flexShrink: 0 } }}>
                {/* Header */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                bgcolor: "primary.lighter",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Iconify icon="mdi:link-variant" width={24} sx={{ color: "primary.main" }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600}>
                            Link Existing User
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={handleClose} aria-label="Close">
                        <Iconify icon="mingcute:close-line" width={24} />
                    </IconButton>
                </Stack>

                <Stack spacing={2.5}>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: "block" }}>
                            Search User
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Search by name or email"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            size="small"
                        />
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, display: "block" }}>
                            User List
                        </Typography>
                        <Scrollbar sx={{ maxHeight: 320 }}>
                            {loading ? (
                                <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
                                    <CircularProgress size={32} />
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Loading users...
                                    </Typography>
                                </Stack>
                            ) : (
                                <Stack spacing={1.5}>
                                    {filteredUsers.map((user) => (
                                        <Box
                                            key={user.id}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1,
                                                border: 1,
                                                borderColor: "divider",
                                                bgcolor: "grey.50",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle2" noWrap>
                                                    {user.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {user.email}
                                                </Typography>
                                            </Box>
                                            {alreadyAddedUserIds.has(user.id) ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ flexShrink: 0, fontStyle: "italic" }}
                                                >
                                                    Already added
                                                </Typography>
                                            ) : (
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                    sx={{ flexShrink: 0 }}
                                                >
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={getRoleForUser(user.id)}
                                                        onChange={(event) =>
                                                            setRoleForUser(user.id, event.target.value as WorkspaceRoleValue)
                                                        }
                                                        sx={{ minWidth: 140 }}
                                                    >
                                                        {WORKSPACE_USER_ROLE_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                                                        onClick={() => handleAddUser(user.id)}
                                                    >
                                                        Add
                                                    </Button>
                                                </Stack>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Scrollbar>
                    </Box>
                </Stack>
            </Box>
        </CenteredModal>
    )
}
